import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarPlus, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function OrganiserRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ org_name: "", contact_name: "", email: "", password: "", country: "", website: "" });
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      const { data } = await api.post("/organiser/register", form);
      setDone(data);
      toast.success("Application received");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setWorking(false); }
  };

  return (
    <section className="section-dark min-h-screen py-16 px-6" data-testid="organiser-register-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <p className="gold-kicker mb-2 flex items-center justify-center gap-2"><CalendarPlus size={13} /> Event Organisers</p>
          <h1 className="font-display text-3xl font-bold text-slate-100">ADD YOUR EVENT TO 10XGLOBAL</h1>
          <p className="mt-3 text-sm text-slate-400">Register your organisation, get admin approval, and publish upcoming conferences, trade fairs and exhibitions to a global business audience.</p>
        </div>
        {done ? (
          <div className="card-surface hairline-gold p-8 text-center" data-testid="organiser-register-success">
            <ShieldCheck size={28} className="text-gold mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-slate-100">APPLICATION UNDER REVIEW</h2>
            <p className="mt-2 text-sm text-slate-400">{done.message}</p>
            <p className="mt-4 text-xs font-mono text-slate-500">Organiser ID: <span className="text-gold">{done.organiser_id}</span></p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/events/organiser/login" data-testid="organiser-goto-login" className="btn-gold text-xs">ORGANISER LOGIN</Link>
              <Link to="/events" data-testid="organiser-back-events" className="btn-outline text-xs">BACK TO EVENTS</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card-surface p-6 md:p-8 space-y-4" data-testid="organiser-register-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.org_name} onChange={set("org_name")} placeholder="Organisation name" required data-testid="organiser-org-name" className="input-dark text-sm" />
              <input value={form.contact_name} onChange={set("contact_name")} placeholder="Contact person" required data-testid="organiser-contact-name" className="input-dark text-sm" />
            </div>
            <input type="email" value={form.email} onChange={set("email")} placeholder="Work email" required data-testid="organiser-email" className="input-dark text-sm w-full" />
            <input type="password" value={form.password} onChange={set("password")} placeholder="Password (min 8 characters)" required data-testid="organiser-password" className="input-dark text-sm w-full" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={form.country} onChange={set("country")} placeholder="Country" data-testid="organiser-country" className="input-dark text-sm" />
              <input value={form.website} onChange={set("website")} placeholder="Website (optional)" data-testid="organiser-website" className="input-dark text-sm" />
            </div>
            <button type="submit" disabled={working} data-testid="organiser-register-submit" className="btn-gold w-full text-xs !py-3.5">
              {working ? <Loader2 size={14} className="animate-spin" /> : "SUBMIT ORGANISER APPLICATION"}
            </button>
            <p className="text-center text-[11px] text-slate-500">Already approved? <Link to="/events/organiser/login" data-testid="organiser-login-link" className="text-gold hover:text-gold-light">Organiser login →</Link></p>
          </form>
        )}
      </div>
    </section>
  );
}
